import React, { useState, useEffect, useContext } from "react";
import { Form, Row, Col, Alert } from "react-bootstrap";
import axios from "axios";

import Preloader from "../../../shared/PreLoaders";
import { Authorization } from "../../../Context/Authorization";

const UpdateVfcRenewal = () => {
  const [vfcId, setVfcId] = useState("");
  const [renewalNo, setRenewalNo] = useState("");
  const [renewalDate, setRenewalDate] = useState("");
  const [uploadDocument, setUploadDocument] = useState(null);
  const [vfcs, setVfcs] = useState([]);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [previousReports, setPreviousReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const Api = process.env.REACT_APP_API;

  const handleFileChange = (e) => {
    setUploadDocument(e.target.files[0]);
  };

  useEffect(() => {
    setLoading(true);
    const fetchVfc = async () => {
      try {
        const config = Authorization();
        const response = await axios.get(`${Api}/api/vfcs`, config);
        const data = response.data.vfcs;
        setVfcs(data);
      } catch (error) {
        console.log("Error fetching Data", error);
      }
      setLoading(false);
    };
    fetchVfc();
  }, [Api]);

  // Fetch previous reports when vfcId changes
  useEffect(() => {
    if (vfcId) {
      handleFetchPreviousReports();
    }
  }, [vfcId]);

  const handleFetchPreviousReports = async () => {
    setLoading(true);
    try {
      const config = Authorization();

      const response = await axios.post(
        `${Api}/api/renewal/previous`,
        { vfcId },
        config
      );

      setPreviousReports(response.data);
      setError(null); // Clear previous errors
    } catch (err) {
      console.error("Error fetching previous reports", err);
      setError("Failed to load previous reports.");
    } finally {
      setLoading(false); // Make sure loading is set to false after request
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    // Create a FormData object to send the file along with other fields
    const formData = new FormData();
    formData.append("vfcId", vfcId); // Fix the key to 'vfcId'
    formData.append("renewalNo", renewalNo);
    formData.append("renewalDate", renewalDate); // Fix the key to 'renewalDate'
    if (uploadDocument) {
      formData.append("uploadDocument", uploadDocument);
    }

    try {
      const config = Authorization(); // Assuming Authorization() provides necessary headers
      const response = await fetch(`${Api}/api/renewal`, {
        method: "POST",
        headers: {
          ...config.headers, // Include authorization headers if necessary
        },
        body: formData,
      });

      const data = await response.json();

      if (response.ok) {
        setSuccessMessage("Renewal created successfully.");
        setError(null);
        setRenewalNo("");
        setRenewalDate("");
        setUploadDocument(null);
        setVfcId("");
        handleFetchPreviousReports(); // Fetch previous reports after successful submission
      } else {
        setError(data.message || "Error submitting form.");
      }
    } catch (error) {
      console.error("Error creating renewal", error);
      setError("Failed to create renewal.");
    }
  };

  return (
    <div>
      <Preloader loading={loading} />
      <div className="page-header">
        <h3 className="page-title">Renewal Report </h3>
        <nav aria-label="breadcrumb">
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="!#" onClick={(event) => event.preventDefault()}>
                VFC
              </a>
            </li>
            <li className="breadcrumb-item active" aria-current="page">
              Renewal Report
            </li>
          </ol>
        </nav>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}
      {successMessage && <Alert variant="success">{successMessage}</Alert>}

      <Row>
        <Col md={5}>
          <div className="col-md-12 grid-margin stretch-card">
            <div className="card">
              <div className="card-body">
                <p className="card-description">VFC Audit Form</p>
                <form className="forms-sample" onSubmit={handleSubmit}>
                  <Form.Group controlId="formVFC">
                    <label htmlFor="vfcId">VFC Name</label>
                    <Form.Control
                      as="select"
                      value={vfcId}
                      onChange={(e) => setVfcId(e.target.value)}
                      required
                    >
                      <option value="">-- Select VFC --</option>
                      {vfcs.map((vfc) => (
                        <option key={vfc._id} value={vfc._id}>
                          {vfc.vfcName}
                        </option>
                      ))}
                    </Form.Control>
                  </Form.Group>
                  <Form.Group controlId="formRenewalNo">
                    <Form.Label>Renewal No</Form.Label>

                    <Form.Control
                      type="text"
                      value={renewalNo}
                      onChange={(e) => setRenewalNo(e.target.value)}
                      required
                    />
                  </Form.Group>
                  <Form.Group controlId="formBalance">
                    <Form.Label>Renewal Date</Form.Label>

                    <Form.Control
                      type="date"
                      value={renewalDate}
                      onChange={(e) => setRenewalDate(e.target.value)}
                      required
                    />
                  </Form.Group>

                  <Form.Group controlId="formDocument">
                    <Form.Label>Upload Document</Form.Label>

                    <Form.Control
                      type="file"
                      name="uploadDocument"
                      onChange={handleFileChange}
                    />
                  </Form.Group>
                  <button
                    type="submit"
                    className="btn btn-outline-primary btn-icon-text mt-3"
                  >
                    <i className="mdi mdi-file-check btn-icon-prepend"></i>
                    Submit
                  </button>
                  <button className="btn btn-light mt-3">Cancel</button>
                </form>
              </div>
            </div>
          </div>
        </Col>

        <Col md={7}>
          <p className="breadcrumb-item active" aria-current="page">
            Previous Reports
          </p>
          <div className="card">
            <div className="card-body">
              <div className="row icons-list">
                <div className="table-responsive">
                  <table striped bordered hover className="table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>VFC Name</th>
                        <th>Renewal Date</th>
                        <th>Renewal No</th>
                      </tr>
                    </thead>
                    <tbody>
                      {previousReports.map((report, index) => (
                        <tr key={index}>
                          <td>{index + 1}</td>
                          <td>{report.vfcId.vfcName}</td>
                          <td>
                            {new Date(report.renewalDate).toLocaleDateString()}
                          </td>
                          <td>{report.renewalNo}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default UpdateVfcRenewal;
